import { useEffect, useState } from "react";
import { FilePlus2, LayoutTemplate } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { SearchPicker } from "../../components/SearchPicker";
import { EventPeriodField } from "./EventPeriodField";
import { useToast } from "../../components/Toast";
import { api } from "../../api/client";
import type { Company, QuoteDetail } from "../../api/types";

// 建立報價單。刻意只問四件事:專案名稱、專案時間、專案地點、客戶名稱 ——
// 其餘欄位進編輯器再補,不要在這裡攔人。
//
// 建完直接進編輯器,因為建單的下一步一定是套範本或開始填價。
//
// 網址帶著 ?template=N 進來時(範本頁的「開新單套用」或 ⌘K 選到範本),
// 建完之後把這個範本一路交棒到編輯器,使用者不必自己再找一次。

export function NewQuotePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const rawTemplate = Number(searchParams.get("template"));
  const templateId = Number.isFinite(rawTemplate) && rawTemplate > 0 ? rawTemplate : null;
  const [title, setTitle] = useState("");
  // 專案時間只選日期,印出來的字由後端產生(業主 2026-09-30)。
  const [eventStart, setEventStart] = useState<string | null>(null);
  const [eventEnd, setEventEnd] = useState<string | null>(null);
  const [venue, setVenue] = useState("");
  const [clientName, setClientName] = useState("");
  // 客戶名稱邊打邊找客戶檔(業主 2026-11-02);點了清單裡的就掛上那家客戶,
  // 沒點就照舊當手打的名字。
  const [clientId, setClientId] = useState<number | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  useEffect(() => {
    api.get<Company[]>("/companies").then((rows) => Array.isArray(rows) && setCompanies(rows)).catch(() => undefined);
  }, []);
  const [saving, setSaving] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (title.trim() === "") {
      toast.push("請先填專案名稱", "error");
      return;
    }
    setSaving(true);
    const picked = companies.find((c) => c.id === clientId) ?? null;
    try {
      const created = await api.post<QuoteDetail>("/quotes", {
        title: title.trim(),
        event_start_date: eventStart,
        event_end_date: eventEnd,
        venue,
        client_company_id: clientId,
        client_name_override: clientId === null ? clientName.trim() || null : null,
        // 掛了客戶就像編輯器選客戶一樣,把主要聯絡人帶進來。
        ...(picked ? { contact_name: picked.contact_name, contact_mobile: picked.contact_mobile,
          contact_phone: picked.contact_phone, contact_email: picked.contact_email } : {}),
      });
      const next = templateId === null ? `/quotes/${created.id}` : `/quotes/${created.id}?apply_template=${templateId}`;
      navigate(next, { replace: true });
    } catch (error) {
      toast.push(error instanceof Error ? error.message : "建立失敗", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <h2 className="m-0 text-xl font-semibold text-[var(--text)]">新增報價單</h2>

      {templateId !== null ? (
        <p className="m-0 flex items-center gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--text-2)]">
          <LayoutTemplate aria-hidden="true" size={16} className="shrink-0 text-[var(--accent)]" />
          建立後會接著套用範本,套用方式與是否帶單價在下一步選。
        </p>
      ) : null}

      <Input
        label="專案名稱"
        value={title}
        placeholder="2026台灣胸腔暨重症加護醫學會年會"
        onChange={(event) => setTitle(event.target.value)}
      />
      <EventPeriodField
        start={eventStart}
        end={eventEnd}
        storedText=""
        originalStart={null}
        originalEnd={null}
        onChange={(start, end) => {
          setEventStart(start);
          setEventEnd(end);
        }}
      />
      <Input label="專案地點" value={venue} onChange={(event) => setVenue(event.target.value)} />
      <SearchPicker
        layout="stacked"
        label="客戶名稱"
        text={clientName}
        placeholder="打字搜尋客戶檔,或直接打名字"
        options={companies.map((c) => ({ key: String(c.id), label: c.name,
          hint: c.tax_id ? `統編 ${c.tax_id}` : undefined, match: `${c.name} ${c.tax_id}` }))}
        onPick={(option) => {
          const company = companies.find((c) => String(c.id) === option.key);
          if (!company) return;
          setClientId(company.id);
          setClientName(company.name);
        }}
        onTextChange={(text) => {
          setClientId(null);
          setClientName(text);
        }}
      />

      <div>
        <Button
          type="submit"
          variant="primary"
          loading={saving}
          icon={<FilePlus2 aria-hidden="true" size={16} />}
        >
          建立報價單
        </Button>
      </div>
    </form>
  );
}
